import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Form,
  Row,
  Modal,
  Table,
} from "react-bootstrap";
import { BsPersonPlusFill } from "react-icons/bs";
import {
  FaEdit,
  FaUsers,
  FaCheckCircle,
  FaWpforms,
  FaDownload,
  FaPrint,
  FaClock,
} from "react-icons/fa";
import { MdAccessTime } from "react-icons/md";
import { IoEye } from "react-icons/io5";
import { TbReplaceFilled } from "react-icons/tb";
import { MdDelete } from "react-icons/md";
import axios from "axios";
import AtdLayout from "../../components/AttendanceLayout.jsx";
import "./current-date.css";

// ──────────────────────────────────────
// UK TIME HELPERS (NO date-fns-tz)
// ──────────────────────────────────────

const formatTimeUK = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Europe/London",
  });
};

const formatDateTimeUK = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleString("en-GB", {
    timeZone: "Europe/London",
  });
};

// Get UK date in yyyy-mm-dd for break API
const getUKDateStr = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-CA", {
    timeZone: "Europe/London",
  });
};

export default function CurrentAtd() {
  // ──────────────────────────────────────
  // STATE
  // ──────────────────────────────────────
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDocUrl, setSelectedDocUrl] = useState("");
  const [attendanceId, setAttendanceId] = useState(null);
  const [docCategory, setDocCategory] = useState("");
  const fileInputRef = useRef(null);

  const [showEarlyLeaveApprovalModal, setShowEarlyLeaveApprovalModal] = useState(false);
  const [showLateApprovalModal, setShowLateApprovalModal] = useState(false);
  const [showOvertimeApprovalModal, setShowOvertimeApprovalModal] = useState(false);
  const [approvalData, setApprovalData] = useState({});
  const [hrSignature, setHrSignature] = useState("");
  const [adminSignature, setAdminSignature] = useState("");
  const [applySingleDeduction, setApplySingleDeduction] = useState(false);

  const [showBreakTimesModal, setShowBreakTimesModal] = useState(false);
  const [breakList, setBreakList] = useState([]);
  const [breakLoading, setBreakLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [hoveredBreaks, setHoveredBreaks] = useState({});

  const printRef = useRef(null);

  // ──────────────────────────────────────
  // FETCH ATTENDANCE
  // ──────────────────────────────────────
  const fetchAttendance = () => {
    setLoading(true);
    fetch(
      `https://backend.ems.uk.hiradentallab.com/api/attendance/attendance/today?search=${searchQuery}`
    )
      .then((response) => response.json())
      .then((fetchedData) => {
        const fetchedDataArr = Array.isArray(fetchedData.data)
          ? fetchedData.data
          : [];
        const processedData = fetchedDataArr.map((item) => ({
          ...item,
          lateReason: item.lateReason ?? "Not provided",
          earlyCloseReason: item.earlyCloseReason ?? "Not provided",
          overtimeReason: item.overtimeReason ?? "Not provided",
          paidBreakLimitMinutes: item.paidBreakLimitMinutes ?? 0,
          unpaidBreakLimitMinutes: item.unpaidBreakLimitMinutes ?? 0,
          usedPaidAllowanceMinutes: item.usedPaidAllowanceMinutes ?? 0,
          usedUnpaidAllowanceMinutes: item.usedUnpaidAllowanceMinutes ?? 0,
          totalPaidBreakMinutes: item.totalPaidBreakMinutes ?? 0,
          totalUnpaidBreakMinutes: item.totalUnpaidBreakMinutes ?? 0,
        }));
        setData(processedData);
        setFilteredData(processedData);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching attendance data:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAttendance();
  }, [searchQuery]);

  // ──────────────────────────────────────
  // BREAK TIMES MODAL
  // ──────────────────────────────────────
  const handleOpenBreakTimesModal = async (item) => {
    setSelectedItem(item);
    setShowBreakTimesModal(true);
    setBreakList([]);
    setBreakLoading(true);

    try {
      const date = getUKDateStr(item.checkIn); // ✅ UK date
      const url = `https://backend.ems.uk.hiradentallab.com/api/attendance/breaks?userId=${item.userId}&date=${date}`;
      const res = await fetch(url);
      const json = await res.json();
      if (json.success) {
        setBreakList(json.data.breaks || json.data.paidBreaks || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setBreakLoading(false);
    }
  };

  // ──────────────────────────────────────
  // BREAK BUTTON RENDER
  // ──────────────────────────────────────
  const renderBreakButton = (item) => {
    const isHovered = hoveredBreaks[item._id] || false;
    return (
      <Button
        variant="primary"
        size="sm"
        style={{
          background: isHovered
            ? "linear-gradient(135deg, #0069d9, #007bff)"
            : "linear-gradient(135deg, #007bff, #0056b3)",
          border: "none",
          color: "#fff",
          fontWeight: 600,
          padding: "6px 16px",
          borderRadius: "20px",
          boxShadow: isHovered
            ? "0 4px 12px rgba(0,123,255,0.4)"
            : "0 2px 6px rgba(0,123,255,0.2)",
          transition: "all 0.3s ease",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
        }}
        onMouseEnter={() => setHoveredBreaks((prev) => ({ ...prev, [item._id]: true }))}
        onMouseLeave={() => setHoveredBreaks((prev) => ({ ...prev, [item._id]: false }))}
        onClick={() => handleOpenBreakTimesModal(item)}
      >
        <FaClock style={{ fontSize: "0.9rem" }} /> Details
      </Button>
    );
  };

  // ──────────────────────────────────────
  // MODAL HANDLERS
  // ──────────────────────────────────────
  const handleShowModal = (data) => {
    setModalData(data);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalData({});
  };

  // ──────────────────────────────────────
  // DOCUMENT VIEW / REPLACE
  // ──────────────────────────────────────
  const handleViewDocument = (docUrl, item, category) => {
    if (!docUrl) return alert("Document not available");
    const formattedUrl = docUrl.startsWith("http")
      ? docUrl
      : `https://backend.ems.uk.hiradentallab.com${docUrl}`;
    setSelectedDocUrl(formattedUrl);
    setDocCategory(category);
    setAttendanceId(item._id);
    setShowViewModal(true);
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(selectedDocUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = selectedDocUrl.split("/").pop() || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Download failed.");
    }
  };

  const handleFileReplace = async (e) => {
    const file = e.target.files[0];
    if (!file || !attendanceId || !docCategory) return alert("Missing file or data");
    const formData = new FormData();
    formData.append("document", file);
    formData.append("uploadCategory", docCategory);

    try {
      const response = await fetch(
        `https://backend.ems.uk.hiradentallab.com/api/attendance/update-document/${attendanceId}`,
        {
          method: "POST",
          body: formData,
        }
      );
      if (response.ok) {
        alert("Document replaced successfully!");
        setShowViewModal(false);
        fetchAttendance();
      } else {
        alert("Failed to replace document");
      }
    } catch (error) {
      alert("Error replacing file.");
    }
  };

  // ──────────────────────────────────────
  // APPROVAL FORMS (Placeholders)
  // ──────────────────────────────────────
  const handleOpenLateApprovalForm = (item) => {
    alert("Late Approval clicked for " + item.userName);
  };

  const handleOpenEarlyLeaveApprovalForm = (item) => {
    alert("Early Leave Approval clicked for " + item.userName);
  };

  const handleOpenOvertimeApprovalForm = (item) => {
    alert("Overtime Approval clicked for " + item.userName);
  };

  // ──────────────────────────────────────
  // DELETE ATTENDANCE
  // ──────────────────────────────────────
  const handleDeleteClick = (attendance) => {
    if (window.confirm(`Delete attendance for ${attendance.userName}?`)) {
      fetch(`https://backend.ems.uk.hiradentallab.com/api/attendance/attendance/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attendanceId: attendance._id }),
      })
        .then((res) => res.json())
        .then(() => fetchAttendance());
    }
  };

  // ──────────────────────────────────────
  // PAGINATION
  // ──────────────────────────────────────
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const totalPages = Math.ceil(data.length / rowsPerPage);
  const paginatedData = data.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // ──────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────
  return (
    <AtdLayout>
      <div>
        <h1 className="current-date-title">
          <FaUsers className="settings-icon" /> Employees Attendance
        </h1>
        <hr />

        <div className="current-date-content">
          <Row>
            <Col xs="auto">
              <Link to="/add-atd">
                <Button variant="primary" style={{ marginBottom: "10px" }}>
                  <BsPersonPlusFill /> Add Attendance
                </Button>
              </Link>
            </Col>
          </Row>

          <div className="current-date-actions">
            <Row>
              <Col xs={12} md={6}>
                <Dropdown as={ButtonGroup}>
                  <Button variant="primary">Show Entries</Button>
                  <Dropdown.Toggle split variant="primary" />
                  <Dropdown.Menu>
                    <Dropdown.Item>10</Dropdown.Item>
                    <Dropdown.Item>25</Dropdown.Item>
                    <Dropdown.Item>50</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col xs={12} md={6} className="d-flex justify-content-end">
                <Form className="d-flex">
                  <Form.Control
                    type="search"
                    placeholder="Search employee..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </Form>
              </Col>
            </Row>
          </div>

          {/* TABLE – UK TIME FORMATTING */}
          <div className="table-responsive">
            {loading ? (
              <p className="text-center">Loading...</p>
            ) : (
              <Table striped hover className="table-style">
                <thead
                  className="yesterday-attendance-table-header"
                  style={{ backgroundColor: "#007bff", color: "#fff" }}
                >
                  <tr className="table-header">
                    <th>SR</th>
                    <th>Employee Name</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Worked Hours</th>
                    <th>Late Minutes</th>
                    <th>Early Minutes</th>
                    <th>Overtime</th>
                    <th>Break Details</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item, index) => (
                    <tr key={item._id || index}>
                      <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                      <td>{item.userName}</td>
                      <td>{formatTimeUK(item.checkIn)}</td>
                      <td>
                        {item.checkOut ? formatTimeUK(item.checkOut) : "At Work"}
                      </td>
                      <td>{item.workedHours}</td>
                      <td>{item.lateMinutes || 0}</td>
                      <td>{item.earlyCloseMinutes || 0}</td>
                      <td>{item.extraMinutes || 0}</td>
                      <td>{renderBreakButton(item)}</td>
                      <td>
                        <Link to={`/edit-atd/${item._id}`}>
                          <Button variant="warning" size="sm" className="me-1">
                            <FaEdit />
                          </Button>
                        </Link>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleShowModal(item)}
                        >
                          <MdAccessTime />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          className="ms-1"
                          onClick={() => handleDeleteClick(item)}
                        >
                          <MdDelete />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="provident-fund-footer">
                  <tr
                    className="provident-fund-table-footer"
                    style={{ backgroundColor: "#007bff", color: "#fff" }}
                  >
                    <td>Total</td>
                    <td>{data.length}</td>
                    <td colSpan="8"></td>
                  </tr>
                </tfoot>
              </Table>
            )}
          </div>

          <div className="pagination-row">
            <div>
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, data.length)} of {data.length} entries
            </div>
            <nav>
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage - 1);
                    }}
                  >
                    Previous
                  </a>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <li
                    key={page}
                    className={`page-item ${currentPage === page ? "active" : ""}`}
                  >
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(page);
                      }}
                    >
                      {page}
                    </a>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage + 1);
                    }}
                  >
                    Next
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* ──────────────── BREAK HISTORY MODAL (UK TIME) ──────────────── */}
  <Modal
  show={showBreakTimesModal}
  onHide={() => setShowBreakTimesModal(false)}
  centered
  size="lg"
>
  <Modal.Header
    closeButton
    style={{ backgroundColor: "#007bff", color: "#fff" }}
  >
    <Modal.Title style={{ color: "#fff" }}>
      <FaClock className="me-2" />
      Break History – {selectedItem?.userName}
    </Modal.Title>
  </Modal.Header>
  <Modal.Body style={{ background: "#f8f9fa", borderRadius: "0 0 10px 10px" }}>
    {breakLoading ? (
      <p className="text-center">Loading breaks...</p>
    ) : (
      <div
        style={{
          background: "#fff",
          borderRadius: "8px",
          padding: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        {/* ── AUTO BREAK DEDUCTION ALERT ── */}
        {selectedItem?.autoBreakApplied && selectedItem?.autoBreakDeduction && (
          <div
            className="alert mb-3"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "#fff",
              borderRadius: "10px",
              border: "none",
              padding: "14px 18px",
              fontSize: "0.9rem",
            }}
          >
            <div className="d-flex align-items-start gap-2">
              <div
                style={{
                  background: "rgba(255,255,255,0.2)",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FaClock style={{ fontSize: "0.85rem" }} />
              </div>
              <div>
                <strong style={{ fontSize: "1rem" }}>
                  Auto Break Deducted
                </strong>
                <p className="mb-1 mt-1" style={{ opacity: 0.9 }}>
                  No manual break recorded. System auto-deducted{" "}
                  <strong>{selectedItem.autoBreakDeduction.totalAutoBreakMinutes} min</strong>{" "}
                  based on schedule.
                </p>
                <div className="d-flex gap-2 flex-wrap mt-2">
                  {selectedItem.autoBreakDeduction.paidMinutes > 0 && (
                    <span
                      className="badge"
                      style={{
                        background: "rgba(255,255,255,0.25)",
                        color: "#fff",
                        fontSize: "0.8rem",
                        padding: "4px 10px",
                        borderRadius: "12px",
                      }}
                    >
                      Paid: {selectedItem.autoBreakDeduction.paidMinutes}m
                    </span>
                  )}
                  {selectedItem.autoBreakDeduction.unpaidMinutes > 0 && (
                    <span
                      className="badge"
                      style={{
                        background: "rgba(255,255,255,0.25)",
                        color: "#fff",
                        fontSize: "0.8rem",
                        padding: "4px 10px",
                        borderRadius: "12px",
                      }}
                    >
                      Unpaid: {selectedItem.autoBreakDeduction.unpaidMinutes}m
                    </span>
                  )}
                  {selectedItem.autoBreakDeduction.excessUnpaidMinutes > 0 && (
                    <span
                      className="badge"
                      style={{
                        background: "rgba(255,255,255,0.25)",
                        color: "#fff",
                        fontSize: "0.8rem",
                        padding: "4px 10px",
                        borderRadius: "12px",
                      }}
                    >
                      Excess: {selectedItem.autoBreakDeduction.excessUnpaidMinutes}m
                    </span>
                  )}
                  {selectedItem.autoBreakDeduction.isSixHourSpecial && (
                    <span
                      className="badge"
                      style={{
                        background: "rgba(255,193,7,0.3)",
                        color: "#ffc107",
                        fontSize: "0.8rem",
                        padding: "4px 10px",
                        borderRadius: "12px",
                      }}
                    >
                      6-Hour Shift Special
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── MANUAL BREAK TABLE ── */}
        {breakList.length === 0 ? (
          !selectedItem?.autoBreakApplied ? (
            <p className="text-center text-muted">No break records found.</p>
          ) : null
        ) : (
          <table className="table table-hover mb-0" style={{ fontSize: "0.9rem" }}>
            <thead style={{ backgroundColor: "#e9ecef" }}>
              <tr>
                <th>#</th>
                <th>Break In</th>
                <th>Break Out</th>
                <th>Total Duration</th>
                <th>Breakdown</th>
              </tr>
            </thead>
            <tbody>
              {breakList.map((b, idx) => (
                <tr key={b._id || idx}>
                  <td>{idx + 1}</td>
                  <td>{formatTimeUK(b.breakIn)}</td>
                  <td>{formatTimeUK(b.breakOut)}</td>
                  <td>
                    <span className="fw-bold">{b.durationMinutes} min</span>
                  </td>
                  <td>
                    {b.paidMinutes > 0 && (
                      <span className="badge bg-success me-1">
                        Paid: {b.paidMinutes}m
                      </span>
                    )}
                    {b.unpaidMinutes > 0 && (
                      <span className="badge bg-warning text-dark me-1">
                        Unpaid: {b.unpaidMinutes}m
                      </span>
                    )}
                    {b.excessUnpaidMinutes > 0 && (
                      <span className="badge bg-danger">
                        Excess: {b.excessUnpaidMinutes}m
                      </span>
                    )}
                    {b.paidMinutes === 0 &&
                      b.unpaidMinutes === 0 &&
                      b.excessUnpaidMinutes === 0 && (
                        <span className="text-muted">—</span>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* ── SUMMARY FOOTER ── */}
        <div
          className="mt-3 p-2"
          style={{
            background: "#f1f3f4",
            borderRadius: "6px",
            fontSize: "0.85rem",
          }}
        >
          <div className="d-flex justify-content-between flex-wrap gap-2">
            <span>
              <strong>Total Break:</strong>{" "}
              {selectedItem?.totalBreakMinutes || 0} min
            </span>
            <span>
              <strong>Paid:</strong>{" "}
              {selectedItem?.totalPaidBreakMinutes || 0} min
            </span>
            <span>
              <strong>Unpaid:</strong>{" "}
              {selectedItem?.totalUnpaidBreakMinutes || 0} min
            </span>
            <span>
              <strong>Excess:</strong>{" "}
              {selectedItem?.totalExcessUnpaidMinutes || 0} min
            </span>
            <span>
              <strong>Deducted:</strong>{" "}
              {selectedItem?.breakDeductionMinutes || 0} min
            </span>
          </div>
        </div>
      </div>
    )}
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowBreakTimesModal(false)}>
      Close
    </Button>
  </Modal.Footer>
</Modal>

      {/* ──────────────── ATTENDANCE DETAILS MODAL (UK FULL DATE/TIME) ──────────────── */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Attendance Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>Name:</strong> {modalData.userName}
          </p>
          <p>
            <strong>Check In:</strong> {formatDateTimeUK(modalData.checkIn)}
          </p>
          <p>
            <strong>Check Out:</strong>{" "}
            {modalData.checkOut ? formatDateTimeUK(modalData.checkOut) : "N/A"}
          </p>
          <p>
            <strong>Worked Hours:</strong> {modalData.workedHours}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ──────────────── DOCUMENT VIEW MODAL ──────────────── */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>View Document</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: "center" }}>
          {selectedDocUrl &&
            (selectedDocUrl.endsWith(".pdf") ? (
              <iframe
                src={selectedDocUrl}
                width="100%"
                height="500px"
                title="Document"
              />
            ) : (
              <img src={selectedDocUrl} alt="Document" style={{ maxWidth: "100%" }} />
            ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={handleDownload}>
            <FaDownload /> Download
          </Button>
          <Button variant="warning" onClick={() => fileInputRef.current.click()}>
            <TbReplaceFilled /> Replace
          </Button>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleFileReplace}
        />
      </Modal>
    </AtdLayout>
  );
}